import React from 'react';
import type { ReachMixMinFirebaseViteKey } from '../config/reachMixRequiredClientEnv';

/**
 * ReachMixConfigurationMissingPage
 *
 * Shown when the SPA shell loads but **Firebase client env** was not baked into the build.
 * This is not a substitute for fixing Cloudflare Pages — it replaces a **blank white screen**
 * with an explicit operator-facing message (stakeholders still should not get URL hype until
 * visual QA passes; see pane1774 swarm notes).
 */
export const ReachMixConfigurationMissingPage: React.FC<{
  missingKeys: ReachMixMinFirebaseViteKey[];
}> = ({ missingKeys }) => {
  return (
    <div
      style={{
        minHeight: '100vh',
        padding: '2rem',
        fontFamily: 'system-ui, sans-serif',
        background: '#0f172a',
        color: '#e2e8f0',
      }}
    >
      <h1 style={{ fontSize: '1.35rem', marginBottom: '0.75rem' }}>ReachMix — configuration missing</h1>
      <p style={{ marginBottom: '1rem', lineHeight: 1.55, maxWidth: 640 }}>
        This deploy is missing required <code style={{ color: '#93c5fd' }}>VITE_FIREBASE_*</code>{' '}
        variables at **build** time. The HTML shell loaded (HTTP 200) but the app cannot safely
        start Firebase — without this gate you would often see a **blank page** after hydration.
      </p>
      <p style={{ marginBottom: '0.75rem', fontWeight: 600 }}>Unset or placeholder keys:</p>
      <ul style={{ marginLeft: '1.25rem', lineHeight: 1.7 }}>
        {missingKeys.map((k) => (
          <li key={k}>
            <code style={{ color: '#fca5a5' }}>{k}</code>
          </li>
        ))}
      </ul>
      <p style={{ marginTop: '1.25rem', lineHeight: 1.55, maxWidth: 640, color: '#94a3b8' }}>
        Fix: set these in **Cloudflare Pages → your project → Settings → Environment variables**,
        then trigger a **new production build** (Vite inlines `import.meta.env` at compile time —
        changing vars alone without rebuild does not update the client bundle). See{' '}
        <code>docs/deployment-checklist.md</code> and <code>docs/ENV-VARS-REFERENCE.md</code>.
      </p>
      <p style={{ marginTop: '1rem', lineHeight: 1.55, maxWidth: 640, color: '#94a3b8' }}>
        Payments (Stripe) also require correct **publishable** key and price IDs in the same build,
        plus **Firebase Functions** secrets for server-side Stripe — checklist covers the split.
      </p>
    </div>
  );
};
